import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import dayjs from "dayjs";
import * as XLSX from "xlsx";
import type { Reservation } from "../api/reservationsAPI";
import type { VenueEvent } from "../api/venueEventsAPI";

// jsPDF built-in font doesn't support extended Latin — transliterate for PDF only
const DIACRITIC_MAP: Record<string, string> = {
  ć: "c", č: "c", š: "s", ž: "z", đ: "dj",
  Ć: "C", Č: "C", Š: "S", Ž: "Z", Đ: "Dj",
  ä: "a", ö: "o", ü: "u", ß: "ss",
  Ä: "A", Ö: "O", Ü: "U",
  á: "a", à: "a", â: "a", ã: "a", å: "a",
  é: "e", è: "e", ê: "e", ë: "e",
  í: "i", ì: "i", î: "i", ï: "i",
  ó: "o", ò: "o", ô: "o", õ: "o",
  ú: "u", ù: "u", û: "u",
  ñ: "n", ý: "y",
};

function pdf(text: string | number | null | undefined): string {
  return String(text ?? "").replace(/[^ -]/g, (ch) => DIACRITIC_MAP[ch] ?? ch);
}

// ─── Event reservations ───────────────────────────────────────────────────────

export function exportEventReservationsPDF(event: VenueEvent, reservations: Reservation[]) {
  const doc = new jsPDF();

  doc.setFontSize(16);
  doc.text(pdf(event.title), 14, 18);
  doc.setFontSize(11);
  doc.setTextColor(100);
  doc.text(`${dayjs(event.date).format("DD.MM.YYYY")}  ·  ${event.time}`, 14, 26);
  doc.text(`Ukupno rezervacija: ${reservations.length}`, 14, 32);
  doc.setTextColor(0);

  const rows = reservations.map((r) => {
    const parts = r.guestName.trim().split(" ");
    const firstName = pdf(parts[0] || "");
    const lastName = pdf(parts.slice(1).join(" ") || "");
    const table = r.eventTableNumber ? String(r.eventTableNumber) : "-";
    const tickets = String(r.ticketsCount || 1);
    const payment =
      r.paymentStatus === "paid"
        ? "Placeno"
        : r.paymentStatus === "partially_paid"
        ? "Djelimicno"
        : "Neplaceno";
    const amount = r.amountPaid ? `${r.amountPaid} ${event.currency}` : "-";
    return [firstName, lastName, table, tickets, payment, amount, r.status, r.guestPhone];
  });

  autoTable(doc, {
    head: [["Ime", "Prezime", "Stol", "Karte", "Placanje", "Iznos", "Status", "Telefon"]],
    body: rows,
    startY: 38,
    styles: { fontSize: 9 },
    headStyles: { fillColor: [59, 130, 246] },
  });

  doc.save(`${pdf(event.title).replace(/\s+/g, "_")}_rezervacije.pdf`);
}

export function exportEventReservationsCSV(event: VenueEvent, reservations: Reservation[]) {
  const rows = reservations.map((r) => {
    const parts = r.guestName.trim().split(" ");
    const payment =
      r.paymentStatus === "paid" ? "Plaćeno"
      : r.paymentStatus === "partially_paid" ? "Djelimično"
      : "Neplaćeno";
    return {
      "Ime":      parts[0] || "",
      "Prezime":  parts.slice(1).join(" ") || "",
      "Stol":     r.eventTableNumber ?? "",
      "Karte":    r.ticketsCount || 1,
      "Plaćanje": payment,
      "Iznos":    r.amountPaid ? `${r.amountPaid} ${event.currency}` : "",
      "Status":   r.status,
      "Telefon":  r.guestPhone,
      "Email":    r.guestEmail || "",
    };
  });
  const ws = XLSX.utils.json_to_sheet(rows);
  ws["!cols"] = Object.keys(rows[0] ?? {}).map((key) => ({
    wch: Math.max(key.length, ...rows.map((r) => String((r as any)[key] ?? "").length)) + 2,
  }));
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, "Rezervacije");
  const slug = event.title.replace(/[^a-zA-Z0-9]/g, "_");
  XLSX.writeFile(wb, `${slug}_rezervacije.xlsx`);
}

export function printEventReservations(event: VenueEvent, reservations: Reservation[]) {
  const rows = reservations
    .map((r) => {
      const parts = r.guestName.trim().split(" ");
      const firstName = parts[0] || "";
      const lastName = parts.slice(1).join(" ") || "";
      const table = r.eventTableNumber ? r.eventTableNumber : "-";
      const payment =
        r.paymentStatus === "paid"
          ? '<span style="color:#059669;font-weight:600">Plaćeno</span>'
          : r.paymentStatus === "partially_paid"
          ? '<span style="color:#d97706;font-weight:600">Djelimično</span>'
          : '<span style="color:#dc2626;font-weight:600">Neplaćeno</span>';
      return `<tr><td>${firstName}</td><td>${lastName}</td><td style="text-align:center">${table}</td><td style="text-align:center">${r.ticketsCount || 1}</td><td>${payment}</td><td>${r.amountPaid ? `${r.amountPaid} ${event.currency}` : "-"}</td><td>${r.status}</td><td>${r.guestPhone}</td></tr>`;
    })
    .join("");

  const win = window.open("", "_blank", "width=900,height=700");
  if (!win) return;
  win.document.write(`
    <!DOCTYPE html>
    <html>
    <head>
      <meta charset="utf-8" />
      <title>${event.title} – Rezervacije</title>
      <style>
        body { font-family: Arial, sans-serif; padding: 24px; color: #111; }
        h1 { font-size: 20px; margin-bottom: 4px; }
        .meta { color: #666; font-size: 13px; margin-bottom: 16px; }
        table { border-collapse: collapse; width: 100%; font-size: 12px; }
        th { background: #3b82f6; color: #fff; padding: 7px 10px; text-align: left; }
        td { padding: 6px 10px; border-bottom: 1px solid #e5e7eb; }
        tr:nth-child(even) td { background: #f9fafb; }
        @media print { body { padding: 0; } }
      </style>
    </head>
    <body>
      <h1>${event.title}</h1>
      <div class="meta">${dayjs(event.date).format("DD.MM.YYYY")} · ${event.time} &nbsp;|&nbsp; Ukupno: ${reservations.length}</div>
      <table>
        <thead><tr><th>Ime</th><th>Prezime</th><th>Stol</th><th>Karte</th><th>Plaćanje</th><th>Iznos</th><th>Status</th><th>Telefon</th></tr></thead>
        <tbody>${rows}</tbody>
      </table>
      <script>window.onload = () => { window.print(); }<\/script>
    </body>
    </html>
  `);
  win.document.close();
}

// ─── Table reservations ───────────────────────────────────────────────────────

export function exportReservationsPDF(reservations: Reservation[], label: string) {
  const doc = new jsPDF();

  doc.setFontSize(16);
  doc.text("Rezervacije stolova", 14, 18);
  doc.setFontSize(11);
  doc.setTextColor(100);
  doc.text(pdf(label), 14, 26);
  doc.text(`Ukupno: ${reservations.length}`, 14, 32);
  doc.setTextColor(0);

  const rows = reservations.map((r) => {
    const table = pdf(r.tableNames?.join(", ") || r.tableName || "-");
    const payment =
      r.paymentStatus === "paid"
        ? "Placeno"
        : r.paymentStatus === "partially_paid"
        ? "Djelimicno"
        : r.paymentStatus
        ? "Neplaceno"
        : "-";
    return [
      pdf(r.guestName),
      r.guestPhone,
      `${r.date} ${r.time}`,
      table,
      String(r.numberOfGuests),
      r.status,
      payment,
    ];
  });

  autoTable(doc, {
    head: [["Ime i prezime", "Telefon", "Datum/Vrijeme", "Stol", "Gosti", "Status", "Placanje"]],
    body: rows,
    startY: 38,
    styles: { fontSize: 9 },
    headStyles: { fillColor: [59, 130, 246] },
  });

  doc.save(`rezervacije_${pdf(label).replace(/[^a-zA-Z0-9]/g, "_")}.pdf`);
}

export function exportReservationsCSV(reservations: Reservation[], label: string) {
  const rows = reservations.map((r) => {
    const table = r.tableNames?.join(", ") || r.tableName || "";
    const payment =
      r.paymentStatus === "paid" ? "Plaćeno"
      : r.paymentStatus === "partially_paid" ? "Djelimično"
      : r.paymentStatus ? "Neplaćeno"
      : "";
    return {
      "Ime i prezime": r.guestName,
      "Telefon":        r.guestPhone,
      "Email":          r.guestEmail || "",
      "Datum":          r.date,
      "Vrijeme":        r.time,
      "Stol":           table,
      "Gosti":          r.numberOfGuests,
      "Status":         r.status,
      "Plaćanje":       payment,
      "Iznos":          r.amountPaid || "",
    };
  });
  const ws = XLSX.utils.json_to_sheet(rows);
  ws["!cols"] = Object.keys(rows[0] ?? {}).map((key) => ({
    wch: Math.max(key.length, ...rows.map((r) => String((r as any)[key] ?? "").length)) + 2,
  }));
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, "Rezervacije");
  const slug = label.replace(/[^a-zA-Z0-9]/g, "_");
  XLSX.writeFile(wb, `rezervacije_${slug}.xlsx`);
}

export function printReservations(reservations: Reservation[], label: string) {
  const rows = reservations
    .map((r) => {
      const table = r.tableNames?.join(", ") || r.tableName || "-";
      const payment =
        r.paymentStatus === "paid"
          ? '<span style="color:#059669;font-weight:600">Plaćeno</span>'
          : r.paymentStatus === "partially_paid"
          ? '<span style="color:#d97706;font-weight:600">Djelimično</span>'
          : r.paymentStatus
          ? '<span style="color:#dc2626;font-weight:600">Neplaćeno</span>'
          : "-";
      return `<tr><td>${r.guestName}</td><td>${r.guestPhone}</td><td>${r.date} ${r.time}</td><td>${table}</td><td style="text-align:center">${r.numberOfGuests}</td><td>${r.status}</td><td>${payment}</td></tr>`;
    })
    .join("");

  const win = window.open("", "_blank", "width=900,height=700");
  if (!win) return;
  win.document.write(`
    <!DOCTYPE html>
    <html>
    <head>
      <meta charset="utf-8" />
      <title>Rezervacije – ${label}</title>
      <style>
        body { font-family: Arial, sans-serif; padding: 24px; color: #111; }
        h1 { font-size: 20px; margin-bottom: 4px; }
        .meta { color: #666; font-size: 13px; margin-bottom: 16px; }
        table { border-collapse: collapse; width: 100%; font-size: 12px; }
        th { background: #3b82f6; color: #fff; padding: 7px 10px; text-align: left; }
        td { padding: 6px 10px; border-bottom: 1px solid #e5e7eb; }
        tr:nth-child(even) td { background: #f9fafb; }
        @media print { body { padding: 0; } }
      </style>
    </head>
    <body>
      <h1>Rezervacije stolova</h1>
      <div class="meta">${label} &nbsp;|&nbsp; Ukupno: ${reservations.length}</div>
      <table>
        <thead><tr><th>Ime i prezime</th><th>Telefon</th><th>Datum/Vrijeme</th><th>Stol</th><th>Gosti</th><th>Status</th><th>Plaćanje</th></tr></thead>
        <tbody>${rows}</tbody>
      </table>
      <script>window.onload = () => { window.print(); }<\/script>
    </body>
    </html>
  `);
  win.document.close();
}

// ─── Internal ─────────────────────────────────────────────────────────────────

function downloadCSV(rows: (string | number)[][], filename: string) {
  const csv = rows
    .map((row) => row.map((cell) => `"${String(cell).replace(/"/g, '""')}"`).join(","))
    .join("\n");
  const blob = new Blob(["﻿" + csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `${filename}.csv`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}
